import { useParams } from 'react-router-dom'

import { Box, Button, Card, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { Amount } from 'modules/project_management/components/Amount'
import { InsightCard } from 'modules/project_management/components/InsightCard'
import CurrentOrEstimatedProfitTooltipMessage from 'modules/project_management/pages/ProjectDetailsPage/tabs/OverviewPage/FinancialDetailsSection/tooltips/CurrentOrEstimatedProfitTooltipMessage'

interface CurrentProfitCardProps {
  billableToDate: number
  isProjectFixedCost: boolean
  costToDate: number
  currency: string
  fixedCostPlannedIncome: number
  fixedCostPlannedCents: number | undefined
  locale: string
  registeredCosts: number
}

const CurrentProfitCard = ({
  billableToDate,
  isProjectFixedCost,
  costToDate,
  currency,
  fixedCostPlannedIncome,
  fixedCostPlannedCents,
  locale,
  registeredCosts,
}: CurrentProfitCardProps) => {
  const { navigation } = useNavigation()
  const { id: projectId } = useParams()

  const currentProfitTooltipMessage = `(${i18n.t(
    'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
  )}) - (${i18n.t(
    'project_management.overview.sections.financial_details.insights.cost_to_date.title'
  )})`

  const percentageProfitCalculation = Math.round(
    (() => {
      if (!fixedCostPlannedCents) return 0

      const currentProfit = fixedCostPlannedIncome - registeredCosts
      const plannedProfit = fixedCostPlannedIncome - fixedCostPlannedCents

      if (plannedProfit === 0) return 0

      return ((currentProfit - plannedProfit) / Math.abs(plannedProfit)) * 100
    })()
  )

  const positiveSymbol = percentageProfitCalculation > 0 ? '+' : ''

  const percentageProfitLabel = `${positiveSymbol}${percentageProfitCalculation}%`

  const currentProfit =
    isProjectFixedCost && fixedCostPlannedIncome
      ? fixedCostPlannedIncome - registeredCosts
      : fixedCostPlannedCents
        ? fixedCostPlannedIncome - registeredCosts
        : billableToDate - costToDate

  return isProjectFixedCost && !fixedCostPlannedIncome ? (
    <Card>
      <Card.Content>
        <Text color="grey1000" size="200" weight="medium" ellipsis whiteSpace="nowrap">
          {isProjectFixedCost && !!fixedCostPlannedCents
            ? i18n.t(
                'project_management.overview.sections.financial_details.insights.current_profit.title_estimated'
              )
            : i18n.t(
                'project_management.overview.sections.financial_details.insights.current_profit.title'
              )}
        </Text>
        <Box justifyContent="center" alignItems="center" width="full" paddingY="s24">
          <Box gap="s12" alignItems="center">
            <Text size="300" weight="normal" color="grey800">
              {i18n.t(
                'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.add_planned_income.label'
              )}
            </Text>
            <Button.Router
              iconName="add"
              type="secondary"
              to={navigation.projects.id.details.settings.fullPath({ id: Number(projectId) })}
            >
              {i18n.t(
                'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.add_planned_income.button'
              )}
            </Button.Router>
          </Box>
        </Box>
      </Card.Content>
    </Card>
  ) : (
    <InsightCard
      info={
        isProjectFixedCost ? (
          <CurrentOrEstimatedProfitTooltipMessage
            currency={currency}
            plannedCosts={fixedCostPlannedCents || 0}
            plannedIncome={fixedCostPlannedIncome}
            registeredCosts={registeredCosts}
          />
        ) : (
          currentProfitTooltipMessage
        )
      }
      titleGap="s6"
      title={
        isProjectFixedCost && !!fixedCostPlannedCents
          ? i18n.t(
              'project_management.overview.sections.financial_details.insights.current_profit.title_estimated'
            )
          : i18n.t(
              'project_management.overview.sections.financial_details.insights.current_profit.title'
            )
      }
    >
      <Box flexDirection="row" gap="s8" alignItems="center" paddingTop="s6" flexWrap="noWrap">
        <Box flexNoShrink>
          <Amount
            amount={currentProfit}
            currency={currency}
            size="400"
            weight="semibold"
            color={currentProfit > 0 ? 'grey1000' : 'moodSuperNegative'}
          />
        </Box>
        <Text size="200" weight="normal" color="grey1000" whiteSpace="nowrap">
          {isProjectFixedCost && !!fixedCostPlannedCents
            ? `/ ${MoneyWithCurrency({
                amount: fixedCostPlannedIncome - (fixedCostPlannedCents || 0),
                currency,
                locale,
              })}`
            : isProjectFixedCost && !fixedCostPlannedCents && !!fixedCostPlannedIncome
              ? ''
              : billableToDate === 0
                ? ''
                : `(${(((billableToDate - costToDate) / billableToDate) * 100).toFixed(2)}%)`}
        </Text>
      </Box>
      {isProjectFixedCost && !!fixedCostPlannedCents && (
        <Box flexDirection="row">
          <Text color="grey800" size="200" weight="semibold" ellipsis whiteSpace="nowrap">
            {percentageProfitLabel}
          </Text>
          <Text color="grey800" size="200" weight="medium" ellipsis whiteSpace="nowrap">
            {'\u00A0'}
            {i18n.t(
              'project_management.overview.sections.financial_details.insights.current_profit.planned_profit_message'
            )}
          </Text>
        </Box>
      )}
    </InsightCard>
  )
}

export default CurrentProfitCard
